import { buildReportAxisColumns } from 'src/components/star-grid/utils/reportAxisColumns';
import { ColDef, ColGroupDef, Theme } from 'ag-grid-community';
import { PnlGridRow, PnlPivotModel } from 'src/components/star-grid/starGridTypes';
import { buildColumnDefsFromModel } from 'src/components/star-grid/utils/pnlGrid';

export const PNL_ROW_HEIGHTS = [23, 30, 38] as const;
const SCENARIO_GAP = 45;
const PERIOD_WIDTH = 78;

export const buildStatementTheme = (theme: Theme): Theme =>
  theme.withParams({
    fontSize: 12,
    headerFontSize: 11,
    cellHorizontalPadding: 8,
    headerColumnBorder: false,
    headerColumnResizeHandleColor: 'transparent',
    wrapperBorder: false,
    wrapperBorderRadius: 0,
    rowBorder: { color: 'var(--sg-report-rule)', width: 1 },
    headerBackgroundColor: 'var(--sg-report-background)',
    backgroundColor: 'var(--sg-report-background)',
    oddRowBackgroundColor: 'var(--sg-report-background)',
    headerTextColor: 'var(--sg-report-accent)',
    borderColor: 'var(--sg-report-rule)'
  });

const scenarioSeparator = (index: number): ColDef<PnlGridRow> => ({
  colId: `scenario-gap-${index}`,
  headerName: '',
  width: SCENARIO_GAP,
  minWidth: SCENARIO_GAP,
  maxWidth: SCENARIO_GAP,
  cellClass: 'sg-report-scenario-gap',
  headerClass: 'sg-report-scenario-gap',
  sortable: false,
  resizable: false,
  suppressMovable: true,
  suppressColumnsToolPanel: true,
  suppressHeaderMenuButton: true,
  suppressNavigable: true,
  valueGetter: () => null
});

const statementColumn = (column: ColDef<PnlGridRow>, showFilters: boolean, isRollup = false): ColDef<PnlGridRow> => ({
  ...column,
  width: PERIOD_WIDTH,
  minWidth: PERIOD_WIDTH,
  flex: undefined,
  headerComponent: 'pnlPeriodHeader',
  headerClass: 'sg-report-period-header',
  cellClass: 'sg-report-number',
  filter: showFilters ? 'agNumberColumnFilter' : false,
  floatingFilter: showFilters,
  // A hierarchy-rollup group row is a REAL row carrying its own SUM(value × weight) from the
  // closure; letting the grid aggregate its children on top would double count. Every other
  // model still rolls children up client-side.
  ...(isRollup ? { aggFunc: undefined } : {}),
  allowedAggFuncs: ['pnlSum']
});

export const buildStatementColumns = (
  model: PnlPivotModel,
  showFilters: boolean,
  compactHeaders = false
): (ColDef<PnlGridRow> | ColGroupDef<PnlGridRow>)[] => {
  if (model.axisColumns)
    return buildReportAxisColumns(model.axisColumns, (column) => statementColumn(column, showFilters, true), scenarioSeparator, compactHeaders);
  return buildColumnDefsFromModel(model.columnGroups, model.calculatedColumns).flatMap((group, index) => {
    if (model.isResolvedAxis && model.columnGroups[index]?.isSpacer) return [scenarioSeparator(index)];
    if (!('children' in group)) return [group];
    const statementGroup: ColGroupDef<PnlGridRow> = {
      ...group,
      headerClass: 'sg-report-scenario-header',
      marryChildren: true,
      children: group.children.map((column) => statementColumn(column as ColDef<PnlGridRow>, showFilters, model.isRollup))
    };
    return !model.isResolvedAxis && index ? [scenarioSeparator(index), statementGroup] : [statementGroup];
  });
};

export const displayReportPeriod = (label: string): string =>
  label
    .replace(/^([A-Za-z]{3}) (\d{2})$/, '$1-20$2')
    .replace(/^(\d{4})\s*Q([1-4])$/, 'Q$2-$1')
    .replace(/^Q([1-4]) (\d{2})$/, 'Q$1-20$2');
